import { db } from '@/db/dexieClient';
import type { Address } from '@/domains/address';
import { liveQuery } from 'dexie';
import { uow, UnitOfWork } from '@/db/unitOfWork';
import { upsertEntities, type SyncableEntity, type UpsertConfig } from '@/services/sync/syncUtils';

const addressByIdQuery = (addressId: number) => db.addresses.where('id').equals(addressId).first();
const allAddressesQuery = () => db.addresses.toArray();

// --- Public API: Get Operations ---
export const getAddressesFromCache = async (): Promise<Address[]> => {
  try {
    return await allAddressesQuery();
  } catch (error) {
    console.error('Error fetching addresses from cache:', error);
    return [];
  }
};

export const getAddressFromCacheById = async (addressId: number): Promise<Address | null> => {
  try {
    return (await addressByIdQuery(addressId)) ?? null;
  } catch (error) {
    console.error('Failed to get address from cache:', error);
    return null;
  }
};

export const getAddressByStopIdFromCache = async (routeStopId: number): Promise<Address | null> => {
  try {
    const routeStop = await db.routeStops.get(routeStopId);
    if (!routeStop) return null;
    const address = await db.addresses.get(routeStop.addressId);
    return address ?? null;
  } catch (error) {
    console.error(`Failed to get address for routeStopId ${routeStopId} from cache:`, error);
    return null;
  }
};

export const observeAddresses = () => liveQuery(async () => (await allAddressesQuery()) ?? []);

export const observeAddressById = (addressId: number) =>
  liveQuery(async () => (await addressByIdQuery(addressId)) ?? null);

export const getAllAddressesFromCache = getAddressesFromCache;

export const getAddressIdsFromCache = async (): Promise<Set<number>> => {
  try {
    const addresses = await allAddressesQuery();
    return new Set(addresses.map((addr) => addr.id));
  } catch (error) {
    console.error('Error fetching address IDs from cache:', error);
    return new Set();
  }
};

// --- Public API: Sync Operations ---
export const upsertAddresses = async (apiAddresses: Address[]): Promise<Address[]> => {
  try {
    const cachedAddresses = await getAllAddressesFromCache();
    // Define the configuration for the address upsert operation
    // Note: Addresses don't have syncStatus/syncedOn fields, so we'll use a simplified config
    // Addresses are reference data and will never be updated from this app
    const config: UpsertConfig<Address & SyncableEntity> = {
      getId: (address) => address.id,
      // Addresses don't need markAsPending since they're never updated from this app
    };

    const { toInsert, toUpdate, toKeepLocal } = upsertEntities(
      apiAddresses as (Address & SyncableEntity)[],
      cachedAddresses as (Address & SyncableEntity)[],
      config
    );

    await uow.runRW([db.addresses], async () => {
      // Insert new addresses
      if (toInsert.length > 0) {
        const plainAddresses = UnitOfWork.toPlain(toInsert);
        await db.addresses.bulkPut(plainAddresses);
      }

      // Update existing addresses with API data
      if (toUpdate.length > 0) {
        const plainAddresses = UnitOfWork.toPlain(toUpdate);
        await db.addresses.bulkPut(plainAddresses);
      }

      // Keep local addresses (shouldn't happen since addresses are never updated from app)
      if (toKeepLocal.length > 0) {
        const plainAddresses = UnitOfWork.toPlain(toKeepLocal);
        await db.addresses.bulkPut(plainAddresses);
      }

      // Don't delete addresses not in API response - addresses are reference data
      // that should accumulate across multiple API calls (route stops, shipments, etc.)
      // Only insert/update, never delete
    });

    // Get updated addresses from cache after upsert
    const updatedAddresses = await getAllAddressesFromCache();

    console.info('[addressRepository] Sync completed:', {
      inserted: toInsert.length,
      updated: toUpdate.length,
      deleted: 0, // Addresses are never deleted - they accumulate as reference data
      keptLocal: toKeepLocal.length,
    });

    // Note: Addresses are never updated from this app, so no sync job enqueue needed

    return updatedAddresses;
  } catch (error) {
    console.error('Failed to upsert addresses:', error);
    throw error;
  }
};
